import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Printer, FilePdf, ArrowClockwise } from "@phosphor-icons/react";
import CalcForm from "@/components/CalcForm";
import ResultsDashboard from "@/components/ResultsDashboard";
import SaveScenarioDialog from "@/components/SaveScenarioDialog";
import { Button } from "@/components/ui/button";
import { DEFAULT_INPUTS } from "@/lib/format";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function usePresetHydration(setInputs) {
  useEffect(() => {
    const raw = sessionStorage.getItem("bab_preset_inputs");
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw);
      setInputs((prev) => ({ ...prev, ...parsed }));
      toast.success("Preset loaded — tune for your client");
    } catch (err) {
      toast.error("Couldn't load the example preset", { description: "Starting from defaults instead." });
    }
    sessionStorage.removeItem("bab_preset_inputs");
  }, [setInputs]);
}

async function downloadPdf(savedName) {
  toast("Preparing PDF…");
  try {
    const { data: advisor } = await axios.get(`${API}/advisor`);
    const { buildPdfFromElement } = await import("@/lib/pdf");
    await buildPdfFromElement({
      elementId: "pdf-root",
      fileName: `build-a-bank-${Date.now()}.pdf`,
      advisor,
      clientName: savedName || "Client Breakdown",
      documentTitle: "Quick Breakdown",
    });
    toast.success("PDF downloaded");
  } catch (e) {
    toast.error("PDF generation failed", { description: e.message });
  }
}

export default function Calculator() {
  const [inputs, setInputs] = useState(DEFAULT_INPUTS);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savedName, setSavedName] = useState("");
  const resultsRef = useRef(null);

  usePresetHydration(setInputs);

  const handleCalculate = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/calculate`, inputs);
      setResult(data);
      toast.success("Breakdown calculated");
      setTimeout(() => {
        resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 120);
    } catch (e) {
      toast.error("Calculation failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setInputs(DEFAULT_INPUTS);
    setResult(null);
    toast("Inputs reset to defaults");
  };

  return (
    <main data-testid="calculator-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-16">
      <div className="grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7 space-y-6 no-print">
          <Intro />
          <CalcForm
            inputs={inputs}
            setInputs={setInputs}
            onCalculate={handleCalculate}
            loading={loading}
          />
        </div>

        <div className="lg:col-span-5" ref={resultsRef}>
          {!result && <EmptyState />}
          {result && (
            <>
              <ResultActions
                inputs={inputs}
                onSaved={setSavedName}
                onPdf={() => downloadPdf(savedName)}
                onReset={handleReset}
              />
              <ResultsDashboard result={result} />
            </>
          )}
        </div>
      </div>
    </main>
  );
}

function Intro() {
  return (
    <div>
      <span className="label-eyebrow">Quick Breakdown</span>
      <h1 className="mt-3 font-serif text-4xl md:text-5xl text-brand-forest leading-tight">
        Plug in the business.<br />
        Show them the gap.
      </h1>
      <p className="mt-4 text-brand-ink/70 max-w-xl">
        Default numbers model a 15-truck Ontario roofer pulling $200K/yr into retained
        earnings. Edit any field and run the breakdown.
      </p>
    </div>
  );
}

function ResultActions({ inputs, onSaved, onPdf, onReset }) {
  return (
    <div className="sticky top-20 z-20 flex flex-wrap gap-2 mb-5 no-print">
      <SaveScenarioDialog inputs={inputs} onSaved={onSaved} />
      <Button
        variant="outline"
        onClick={() => window.print()}
        data-testid="btn-print"
        className="border-brand-line text-brand-forest hover:bg-brand-cream rounded-md gap-2"
      >
        <Printer size={16} weight="bold" /> Print
      </Button>
      <Button
        variant="outline"
        onClick={onPdf}
        data-testid="btn-pdf"
        className="border-brand-line text-brand-forest hover:bg-brand-cream rounded-md gap-2"
      >
        <FilePdf size={16} weight="bold" /> Download PDF
      </Button>
      <Button
        variant="ghost"
        onClick={onReset}
        data-testid="btn-reset"
        className="text-brand-muted hover:text-brand-forest rounded-md gap-2"
      >
        <ArrowClockwise size={16} weight="bold" /> Reset
      </Button>
    </div>
  );
}

function EmptyState() {
  return (
    <div
      data-testid="empty-state"
      className="bg-white border border-brand-line border-dashed rounded-md p-10 text-center"
    >
      <div className="mx-auto w-14 h-14 rounded-full bg-brand-cream flex items-center justify-center text-brand-forest font-serif text-2xl">
        $
      </div>
      <h3 className="mt-5 font-serif text-2xl text-brand-forest">Your breakdown appears here</h3>
      <p className="mt-3 text-sm text-brand-ink/60 max-w-xs mx-auto">
        Fill in the four sections on the left and hit <span className="font-semibold">Run breakdown</span>.
        The 10-year wealth gain, chart, and year-by-year table land right here.
      </p>
    </div>
  );
}
